import appConstants from '../../../../../../../appConstants';
import { mergeUserConfig } from '@dataGrid/gridUtilities';
import {
  MultiSelectFilter,
  StaticFilter
} from '@dataGrid/components/columnFilters';
import { SHIPMENT_ISSUES, getShipmentIssuesString } from '../../../utils';
import { RatingRenderer, ShipmentIssuesRenderer } from '../CellRenderers';

export default class GridConfig {
  config: any;

  constructor(configuration: any) {
    this.config = GridConfig.getConfiguration(configuration);
  }

  static getConfiguration({userConfig}: any) {
    const defaultConfig = GridConfig.getDefaultConfiguration();
    return mergeUserConfig({
      defaultConfig,
      userConfig
    });
  }

  static CommentsValueGetter = ({ data }: { data: any }) => {
    return data.overallQualityRatingComments || null;
  };

  static CommentsTooltipValueGetter = ({ data }: { data: any }) => {
    const { overallQualityRatingComments } = data;
    return overallQualityRatingComments || null;
  };

  static ShipmentIssuesValueGetter = ({ data }: { data: any }) => {
    const { shipmentIssues } = data;
    return getShipmentIssuesString(shipmentIssues);
  };

  static RecoupedValueGetter = ({ data }: { data: any }) => {
    const { isTrailerRecouped } = data;
    return isTrailerRecouped ? 'Yes' : 'No';
  };

  static OverallQualityValueGetter = ({ data }: { data: any }) => {
    const { overallQualityRating } = data;
    return overallQualityRating || '-';
  };

  static getOverallQualityFilterOptions = () => {
    return Array.from({ length: 5 }, (_, i) => ({
      value: i + 1,
      text: (i + 1).toString()
    }));
  };

  static getBooleanFilterOptions = () => {
    return [
      { value: 'Yes', text: 'Yes' },
      { value: 'No', text: 'No' }
    ];
  };

  static getShipmentIssuesFilterOptions = () => {
    const issues = SHIPMENT_ISSUES.map((issue) => issue.label);
    issues.push('None');
    return issues.map((issue) => ({
      value: issue,
      text: issue
    }));
  };

  static shipmentIssuesFilterFunction = ({
    value,
    selectedOptions
  }: {
    value: string;
    selectedOptions: string[];
  }) => {
    // Check if any of the selected damage option is present in the value
    return selectedOptions.some((option) => value.includes(option));
  };

  static getDefaultConfiguration() {
    return {
      title: appConstants.gridNames.routeAuditRoutes,
      keyColumn: 'routeID',
      rowSelection: 'multiple',
      rowDeselection: true,
      columns: [
        {
          headerName: 'Trailer',
          field: 'trailerNumber',
          headerTooltip: 'Trailer Number',
          minWidth: appConstants.columnWidth.W_120,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Route',
          field: 'routeName',
          headerTooltip: 'Route Name',
          minWidth: appConstants.columnWidth.W_120,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Driver',
          field: 'driverName',
          headerTooltip: 'Driver Name',
          minWidth: appConstants.columnWidth.DRIVER_DRIVER_NAME,
          tooltipField: 'driverName',
          filter: MultiSelectFilter,
          filterParams:{
            width: appConstants.columnWidth.W_300
          }
        },
        {
          headerName: 'Driver ID',
          field: 'driverNumber',
          headerTooltip: 'Driver ID',
          minWidth: appConstants.columnWidth.DRIVER_NUMBER,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Terminal',
          field: 'terminalCode',
          headerTooltip: 'Terminal',
          minWidth: appConstants.columnWidth.W_90,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Shipment Issues',
          field: 'shipmentIssues',
          headerTooltip: 'Shipment Issues',
          valueGetter: GridConfig.ShipmentIssuesValueGetter,
          cellRenderer: ShipmentIssuesRenderer,
          minWidth: appConstants.columnWidth.W_300,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getShipmentIssuesFilterOptions,
            filterFunction: GridConfig.shipmentIssuesFilterFunction
          }
        },
        {
          headerName: 'Recouped',
          headerTooltip: 'Recouped',
          valueGetter: GridConfig.RecoupedValueGetter,
          minWidth: appConstants.columnWidth.W_100,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getBooleanFilterOptions
          }
        },
        {
          headerName: 'Overall Quality',
          field: 'overallQualityRating',
          headerTooltip: 'Overall Quality Rating',
          valueGetter: GridConfig.OverallQualityValueGetter,
          cellRenderer: RatingRenderer,
          cellClass: 'gridCell-flexStartAlign',
          minWidth: appConstants.columnWidth.W_120,
          maxWidth: appConstants.columnWidth.W_120,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getOverallQualityFilterOptions
          }
        },
        {
          headerName: 'Comments',
          field: 'overallQualityRatingComments',
          headerTooltip: 'Comments',
          valueGetter: GridConfig.CommentsValueGetter,
          minWidth: appConstants.columnWidth.W_300,
          tooltipValueGetter: GridConfig.CommentsTooltipValueGetter,
          flex: 1,
          filter: MultiSelectFilter,
          filterParams:{
            width: appConstants.columnWidth.W_300
          }
        }
      ]
    };
  }
}
